import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeftOutlined, DownloadOutlined } from "@ant-design/icons";
import {
  deletePunchListItem,
  downloadPunchListReport,
  fetchPunchListItems,
  updatePunchListItem,
} from "../services/punchListsAPI";
import { useAuth } from "../hooks/useAuth";
import { useEffect, useRef, useState } from "react";
import {
  Button,
  Dropdown,
  Input,
  Menu,
  Popconfirm,
  Space,
  Table,
  Tag,
  Select,
  notification,
  message,
} from "antd";
import { EditOutlined, SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import queryClient from "../queryClient";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import CreatePunchListItemModal from "../components/CreatePunchListItem";
import UpdatePunchListItemModal from "../components/UpdatePunchListItem";
import ViewPunchListItemModal from "../components/ViewPunchListItemModal";
import { fetchProjectUsers } from "../services/projectsAPI";
import { downloadBlob } from "../utils/downloadUtils";
import { getPreferredPageSize, savePreferredPageSize } from "../utils/paginationUtils";

const PunchListItems = () => {
  const { punchListId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const punchListItemIdFromUrl = searchParams.get("punch_list_item_id");
  const projectIdFromUrl = searchParams.get("project_id");
  const { selectedProject, setSelectedProject, currentUser } = useAuth();
  const userProjectIds =
    currentUser?.projects?.map((p) => p.project_id).join(",") ?? "";
  const projectId = selectedProject?.project_id;
  const projectName = selectedProject?.project_name;
  const userRole = selectedProject?.role;
  const hasFullPunchListAccess =
    userRole === "owner" ||
    userRole === "project_manager" ||
    userRole === "field";
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [viewPunchListItemId, setViewPunchListItemId] = useState(null);
  const [selectedPunchListItem, setSelectedPunchListItem] = useState(null);
  const [localPunchListItems, setLocalPunchListItems] = useState([]);
  const [columnFilters, setColumnFilters] = useState({});
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const searchInput = useRef(null);
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: getPreferredPageSize("punch_list_items"),
    total: 0,
  });

  const {
    data: punchListItemsData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [
      "punch-list-items",
      punchListId,
      pagination.current,
      pagination.pageSize,
      columnFilters,
    ],
    queryFn: () =>
      fetchPunchListItems(
        punchListId,
        pagination.current,
        pagination.pageSize,
        columnFilters,
      ),
    enabled: !!punchListId,
    keepPreviousData: true,
  });

  const { data: projectUsers } = useQuery({
    queryKey: ["projectUsers", projectId, 1, 1000],
    queryFn: () => fetchProjectUsers(projectId, 1, 1000),
    enabled: !!projectId,
  });

  const deleteMutation = useMutation({
    mutationFn: deletePunchListItem,
    onSuccess: () => {
      queryClient.invalidateQueries(["punch-list-items"]);
      notification.success({ message: "Punch list item deleted successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to delete punch list item",
        description: error.message,
      });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ punchListItemId, status }) =>
      updatePunchListItem({
        punchListId,
        punchListItemId,
        punchListItem: { status },
        emailNotifications: false,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["punch-list-items"]);
      notification.success({ message: "Status updated successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update status",
        description: error.response?.data?.detail || error.message,
      });
    },
  });

  const enrichPunchListItems = (items, usersList) =>
    items.map((elem) => {
      const assignees = usersList
        ?.filter((user) => elem?.assignee_ids?.includes(user.id || user._id))
        .map((user) => user.username)
        .join(" ");
      const formatted_assignees = usersList
        ?.filter((user) => elem?.assignee_ids?.includes(user.id || user._id))
        .map((user) => user.username)
        .join(",");
      const approver = usersList?.find(
        (user) => user.id === elem.approver_id || user._id === elem.approver_id,
      );
      return {
        ...elem,
        approver_name: approver?.username,
        assignees,
        formatted_assignees,
      };
    });

  useEffect(() => {
    const raw = punchListItemsData?.items;
    if (!raw) {
      setLocalPunchListItems([]);
      return;
    }
    if (!projectUsers?.data) {
      setLocalPunchListItems(raw.map((elem) => ({ ...elem })));
      return;
    }
    setLocalPunchListItems(enrichPunchListItems(raw, projectUsers.data));
  }, [punchListItemsData, projectUsers]);

  useEffect(() => {
    if (!projectIdFromUrl || !currentUser?.projects?.length) return;
    if (selectedProject?.project_id === projectIdFromUrl) return;
    const match = currentUser.projects.find(
      (p) => p.project_id === projectIdFromUrl,
    );
    if (match) {
      setSelectedProject(match);
    }
  }, [
    projectIdFromUrl,
    userProjectIds,
    selectedProject?.project_id,
    setSelectedProject,
    currentUser?.projects,
  ]);

  useEffect(() => {
    if (!punchListItemIdFromUrl || !punchListId) return;
    if (
      projectIdFromUrl &&
      selectedProject?.project_id !== projectIdFromUrl
    ) {
      return;
    }
    setViewPunchListItemId(punchListItemIdFromUrl);
    setIsViewModalVisible(true);
    const next = new URLSearchParams(searchParams);
    next.delete("punch_list_item_id");
    next.delete("project_id");
    setSearchParams(next, { replace: true });
  }, [
    punchListItemIdFromUrl,
    punchListId,
    projectIdFromUrl,
    selectedProject?.project_id,
    searchParams,
    setSearchParams,
  ]);

  if (isError) {
    notification.error({
      message: "Failed to load punch list items",
      description: error.message,
    });
  }

  const handleUpdateClick = (punchListItem) => {
    setSelectedPunchListItem(punchListItem);
    setIsUpdateModalVisible(true);
  };

  const handleViewClick = (punchListItem) => {
    setViewPunchListItemId(punchListItem.id || punchListItem._id);
    setIsViewModalVisible(true);
  };

  const handleViewModalClose = () => {
    setIsViewModalVisible(false);
    setViewPunchListItemId(null);
  };

  const handleDeleteClick = (punchListItemId) => {
    deleteMutation.mutate({ punchListId, punchListItemId });
  };

  const handleStatusChange = (punchListItemId, newStatus) => {
    updateStatusMutation.mutate({ punchListItemId, status: newStatus });
  };

  /** Per-column highlight from active server filter (same idea as Submittals searchTexts). */
  const renderHighlightedText = (value, filterKey) => {
    const raw = columnFilters[filterKey];
    const term =
      raw != null && String(raw).trim() !== "" ? String(raw).trim() : "";
    const text = value != null && value !== "" ? String(value) : "";
    if (!term) {
      return text;
    }
    return (
      <Highlighter
        highlightStyle={{
          backgroundColor: "#ffc069",
          padding: 0,
        }}
        searchWords={[term]}
        autoEscape
        textToHighlight={text}
      />
    );
  };

  const applyColumnFilter = (selectedKeys, confirm, filterKey) => {
    confirm();
    setPagination((p) => ({ ...p, current: 1 }));
    const raw = selectedKeys[0];
    const v = raw != null && String(raw).trim() !== "" ? String(raw) : "";
    if (v) {
      setColumnFilters((prev) => ({ ...prev, [filterKey]: v }));
    } else {
      setColumnFilters((prev) => {
        const next = { ...prev };
        delete next[filterKey];
        return next;
      });
    }
  };

  const resetColumnFilter = (clearFilters, confirm, filterKey) => {
    clearFilters?.();
    setPagination((p) => ({ ...p, current: 1 }));
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[filterKey];
      return next;
    });
    confirm();
  };

  const getColumnFilterProps = (filterKey) => ({
    filteredValue:
      columnFilters[filterKey] != null && columnFilters[filterKey] !== ""
        ? [columnFilters[filterKey]]
        : null,
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${filterKey}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            applyColumnFilter(selectedKeys, confirm, filterKey)
          }
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => applyColumnFilter(selectedKeys, confirm, filterKey)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => resetColumnFilter(clearFilters, confirm, filterKey)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const columns = [
    {
      title: "Number",
      dataIndex: "number",
      key: "number",
      width: "10%",
      ...getColumnFilterProps("number"),
      render: (text) => renderHighlightedText(text, "number"),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: "30%",
      ...getColumnFilterProps("title"),
      render: (text) => renderHighlightedText(text, "title"),
    },
    {
      title: "Approver",
      dataIndex: "approver_name",
      key: "approver_name",
      width: "20%",
      ...getColumnFilterProps("approver_name"),
      render: (text) => renderHighlightedText(text, "approver_name"),
    },
    {
      title: "Assignees",
      dataIndex: "formatted_assignees",
      key: "formatted_assignees",
      width: "20%",
      ...getColumnFilterProps("formatted_assignees"),
      render: (text) => renderHighlightedText(text, "formatted_assignees"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "20%",
      ...getColumnFilterProps("status"),
      render: (status, record) => {
        const getStatusColor = (s) => {
          return s === "Complete" ? "default" : "orange";
        };

        const completeLabel = renderHighlightedText("Complete", "status");
        const incompleteLabel = renderHighlightedText("Incomplete", "status");

        if (!hasFullPunchListAccess) {
          return (
            <Tag color={getStatusColor(status)} className="m-0">
              {renderHighlightedText(status, "status")}
            </Tag>
          );
        }

        return (
          <Select
            value={status}
            onChange={(value) =>
              handleStatusChange(record.id || record._id, value)
            }
            className="status-select-tag w-[120px]"
            loading={updateStatusMutation.isPending}
            disabled={updateStatusMutation.isPending}
            popupRender={(menu) => menu}
            suffixIcon={<span className="text-xs text-gray-400 ml-1">▼</span>}
          >
            <Select.Option value="Complete">
              <Tag color="default" className="m-0 w-full text-center block">
                {completeLabel}
              </Tag>
            </Select.Option>
            <Select.Option value="Incomplete">
              <Tag color="orange" className="m-0 w-full text-center block">
                {incompleteLabel}
              </Tag>
            </Select.Option>
          </Select>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        if (!hasFullPunchListAccess) {
          return (
            <Button
              type="primary"
              ghost
              size="medium"
              style={{ minWidth: "140px", whiteSpace: "nowrap" }}
              onClick={() => handleViewClick(record)}
            >
              View / Respond
            </Button>
          );
        }

        return (
          <Dropdown
            overlay={
              <Menu>
                {hasFullPunchListAccess && (
                  <>
                    <Menu.Item
                      key="view"
                      onClick={() => handleViewClick(record)}
                    >
                      <Button className="w-full">View</Button>
                    </Menu.Item>
                    <Menu.Item
                      key="1"
                      onClick={() => handleUpdateClick(record)}
                    >
                      <Button className="w-full">Update</Button>
                    </Menu.Item>
                    <Menu.Item key="2">
                      <Popconfirm
                        title="Are you sure you want to delete this Punch List Item?"
                        onConfirm={() =>
                          handleDeleteClick(record.id || record._id)
                        }
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button className="w-full" danger>
                          Delete
                        </Button>
                      </Popconfirm>
                    </Menu.Item>
                  </>
                )}
              </Menu>
            }
            trigger={["click"]}
          >
            <Button icon={<EditOutlined />} size="medium"></Button>
          </Dropdown>
        );
      },
    },
  ];

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    savePreferredPageSize(newPagination.pageSize, "punch_list_items");
  };

  const handleExportPDF = async () => {
    const exportKey = "exportingPunchListPdf";
    try {
      const total = punchListItemsData?.total ?? 0;
      if (!total) {
        notification.warning({ message: "No data to export" });
        return;
      }

      setIsExportingPdf(true);
      message.loading({
        content: "Generating punch list report...",
        key: exportKey,
        duration: 0,
      });

      const blob = await downloadPunchListReport(punchListId, columnFilters);

      const safeProject = (projectName || "project")
        .replace(/[^a-zA-Z0-9-_ ]/g, "")
        .replace(/ /g, "_");
      const today = new Date().toISOString().slice(0, 10);
      const filename = `${safeProject}-punch-list-${today}.pdf`;

      downloadBlob(blob, filename);
      message.destroy(exportKey);
      notification.success({
        message: "PDF exported successfully",
        description: `${total} item report${total === 1 ? "" : "s"} merged`,
      });
    } catch (error) {
      message.destroy(exportKey);
      notification.error({
        message: "Failed to export PDF",
        description:
          error.response?.data?.detail || error.message || "Failed to export PDF",
      });
    } finally {
      setIsExportingPdf(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Punch List Items</h1>
      <div className="flex gap-2">
        <Button
          type="primary"
          onClick={() => navigate("/punch-lists")}
          className="mb-4"
          icon={<ArrowLeftOutlined />}
        />

        {hasFullPunchListAccess && (
          <Button
            type="primary"
            onClick={() => setIsCreateModalVisible(true)}
            className="mb-4"
          >
            Create
          </Button>
        )}

        <Button
          icon={<DownloadOutlined />}
          onClick={handleExportPDF}
          loading={isExportingPdf}
          disabled={isExportingPdf}
          className="mb-4"
        >
          Export PDF
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={localPunchListItems}
        rowKey="id"
        loading={isLoading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: punchListItemsData?.total,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        onChange={handleTableChange}
      />

      <CreatePunchListItemModal
        visible={isCreateModalVisible}
        onClose={() => setIsCreateModalVisible(false)}
        punchListId={punchListId}
      />

      {selectedPunchListItem && (
        <UpdatePunchListItemModal
          visible={isUpdateModalVisible}
          onClose={() => setIsUpdateModalVisible(false)}
          punchListId={punchListId}
          punchListItem={selectedPunchListItem}
        />
      )}

      <ViewPunchListItemModal
        visible={isViewModalVisible}
        onClose={handleViewModalClose}
        punchListId={punchListId}
        punchListItemId={viewPunchListItemId}
      />
    </div>
  );
};

export default PunchListItems;
